import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { linkControlledHoverEffect } from '@hivebrite/design-system/Theme';

import ProgressBar from 'hb-react/frontoffice/components/ProgressBar';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';
import Check from 'hb-react/shared/icons/Check';
import onEnterOrSpacePress from 'hb-react/shared/utils/onEnterOrSpacePress';

const DonationsCard = ({
  coverPicture,
  fallbackCoverPicture,
  goalAmount,
  onClick,
  title,
  totalAmount,
  currency,
  enableProgressBar,
}) => {
  const isFunded = totalAmount >= goalAmount;

  const showProgressBar = goalAmount > 0 && enableProgressBar;

  return (
    <StyledCard
      onClick={onClick}
      onKeyPress={onEnterOrSpacePress(onClick)}
      tabIndex={0}
    >
      <ImgWrapper>
        <StyledImg
          $source={coverPicture}
          $fallbackCoverPicture={fallbackCoverPicture}
        />
      </ImgWrapper>
      <Box
        px={{ _: 3, l: 4 }}
        pt={4}
        pb={{ _: 5, xs: showProgressBar && 5 }}
        flex={1}
        width="100%"
        zIndex={1}
      >
        <Box flexDirection="column" width="100%" data-testid="link-to-donation">
          <ScreenReadersContent>
            {s__('Accessibility|Click to see the donation %{donationName}').i__(
              {
                donationName: title,
              },
            )}
          </ScreenReadersContent>
          <Box
            flexDirection="column"
            height="100%"
            justifyContent="space-between"
          >
            <HeaderTitleStyled pl={{ s: 1 }} mb={{ s: !showProgressBar && 3 }}>
              <ClampedLinkWorkaround clamp={2} padding="2">
                {title}
              </ClampedLinkWorkaround>
            </HeaderTitleStyled>
            <Box flexDirection="column" pt={isFunded ? 2 : 4}>
              {showProgressBar && (
                <>
                  <Box alignItems="center">
                    <ProgressBar
                      percentWidth={(totalAmount / goalAmount) * 100}
                    />
                    {isFunded && (
                      <StyledCheckMark>
                        <Check width={16} height={16} color="white" />
                      </StyledCheckMark>
                    )}
                  </Box>
                  <Box mt={!isFunded && 2} color="primary">
                    {s__('Donations|%{symbol}%{totalAmount} ').i__({
                      totalAmount,
                      symbol: currency.symbol,
                    })}{' '}
                    {s__('Donations| of %{symbol}%{goalAmount} funded').i__({
                      symbol: currency.symbol,
                      goalAmount,
                    })}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </StyledCard>
  );
};

DonationsCard.propTypes = {
  coverPicture: PropTypes.string.isRequired,
  fallbackCoverPicture: PropTypes.string,
  goalAmount: PropTypes.number,
  onClick: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  totalAmount: PropTypes.number,
};

export default DonationsCard;

const StyledImg = styled(Box).attrs({
  width: '100%',
  height: '100%',
  tabIndex: -1,
})`
  cursor: pointer;

  ${({ $source, $fallbackCoverPicture }) => css`
    background-image: url('${$source}'), url(${$fallbackCoverPicture});
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
  `}
`;

const HeaderTitleStyled = styled(Box).attrs({
  font: 'h3-semibold',
})`
  text-decoration: none;
`;

const StyledCheckMark = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'primary',
  height: '25px',
  minWidth: '25px',
  marginLeft: '5px',
}))`
  border-radius: 50%;
`;

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  padding: 0;
  position: relative;
  cursor: pointer;
  height: 100%;
  &:hover ${HeaderTitleStyled} {
    ${linkControlledHoverEffect()}
  }
  & ${StyledImg} {
    transition: transform 0.3s;
    transform: scale(1);
  }
  &:hover ${StyledImg} {
    transform: scale(1.05);
  }
  & a:hover {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const ImgWrapper = styled(Box).attrs(() => ({
  position: 'relative',
  width: '100%',
  overflow: 'hidden',
}))`
  background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
  border-top-left-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
  border-top-right-radius: ${({ theme }) =>
    `${theme.themeVariables.globalBorderRadius}px`};
  aspect-ratio: 652 / 350;
`;
